import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../@services/toast.service';
import {
  getEffectiveRows,
  visibleFieldsFor,
  visibleHeadersFor,
  rowsToJson,
  rowsToDelimited,
  copyToClipboard,
} from '../ag-grid-export';

// Inline Copy dropdown for the common-data grids. Reads
// `getEffectiveRows(gridApi)` — selected rows first, else every
// row visible after filter+sort. Multi-row copies include a header
// line for CSV/TSV per Mike's spec.
@Component({
  standalone: true,
  selector: 'app-grid-copy-menu',
  imports: [CommonModule],
  template: `
  <div class="relative inline-block">
    <button type="button" class="dark-btn dark-btn-sm"
            (click)="open = !open; $event.stopPropagation()"
            [disabled]="!gridApi"
            [title]="'Copy selected rows to clipboard'">
      <i class="fa-regular fa-copy mr-1"></i>
      Copy
      <i class="fa-solid fa-caret-down ml-1 text-xs opacity-70"></i>
    </button>
    <ng-container *ngIf="open">
      <div class="fixed inset-0 z-0" (click)="open = false"></div>
      <div class="absolute right-0 z-10 mt-1 min-w-[200px] rounded border border-gray-200 dark:border-[#434863] bg-white dark:bg-app-black-100 shadow-lg py-1 text-sm">
        <button type="button"
                class="w-full text-left px-3 py-2 flex items-center gap-2 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-app-blue-100/30 transition"
                (click)="doCopy('json')">
          <i class="fa-solid fa-fw fa-code text-app-teal-100"></i>
          <span>Copy as JSON</span>
          <span class="ml-auto text-xs opacity-70">{{ rowCountLabel() }}</span>
        </button>
        <button type="button"
                class="w-full text-left px-3 py-2 flex items-center gap-2 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-app-blue-100/30 transition"
                (click)="doCopy('csv')">
          <i class="fa-solid fa-fw fa-file-csv text-emerald-400"></i>
          <span>Copy as CSV</span>
          <span class="ml-auto text-xs opacity-70">{{ rowCountLabel() }}</span>
        </button>
        <button type="button"
                class="w-full text-left px-3 py-2 flex items-center gap-2 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-app-blue-100/30 transition"
                (click)="doCopy('tsv')">
          <i class="fa-solid fa-fw fa-grip-lines-vertical text-amber-400"></i>
          <span>Copy as TSV (tab-delimited)</span>
          <span class="ml-auto text-xs opacity-70">{{ rowCountLabel() }}</span>
        </button>
      </div>
    </ng-container>
  </div>
  `,
})
export class GridCopyMenuComponent {
  @Input() gridApi: any = null;

  open = false;

  constructor(private toast: ToastService) {}

  private effective(): any[] {
    return getEffectiveRows(this.gridApi);
  }

  rowCountLabel(): string {
    const rows = this.effective();
    if (rows.length === 0) return '(0)';
    const selected = this.gridApi?.getSelectedRows?.()?.length || 0;
    const noun = selected > 0 ? 'selected' : 'visible';
    return `(${rows.length} ${noun})`;
  }

  async doCopy(fmt: 'json' | 'csv' | 'tsv') {
    const rows = this.effective();
    if (rows.length === 0) {
      this.toast.warning('Nothing to copy', 'No rows selected or visible.');
      this.open = false;
      return;
    }
    let text = '';
    if (fmt === 'json') {
      text = rowsToJson(rows);
    } else {
      const fields = visibleFieldsFor(this.gridApi);
      const headers = visibleHeadersFor(this.gridApi);
      text = rowsToDelimited(rows, fields, headers, fmt === 'csv' ? ',' : '\t');
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      this.toast.success('Copied', `${rows.length} row${rows.length === 1 ? '' : 's'} as ${fmt.toUpperCase()}`);
    } else {
      this.toast.error('Copy failed', 'Clipboard API rejected the write (browser permissions?).');
    }
    this.open = false;
  }
}
